import React from "react";

/** Parses common social-video URLs into something embeddable.
 * Supported: YouTube (watch / youtu.be / shorts), Instagram (post / reel),
 * Facebook (post / video / watch).
 * Returns { type, embedUrl, fallback?: string }.
 */
export function parseVideoUrl(rawUrl) {
  if (!rawUrl) return null;
  const url = rawUrl.trim();

  // YouTube
  let m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  if (m) {
    return { type: "youtube", embedUrl: `https://www.youtube.com/embed/${m[1]}` };
  }

  // Instagram (post or reel)
  m = url.match(/instagram\.com\/(?:p|reel|tv)\/([\w-]+)/i);
  if (m) {
    return { type: "instagram", embedUrl: `https://www.instagram.com/p/${m[1]}/embed` };
  }

  // Facebook (post / video / reel / watch)
  if (/facebook\.com\//i.test(url) || /fb\.watch\//i.test(url)) {
    const enc = encodeURIComponent(url);
    return {
      type: "facebook",
      embedUrl: `https://www.facebook.com/plugins/video.php?href=${enc}&show_text=0`,
    };
  }

  return { type: "unknown", embedUrl: null, fallback: url };
}

/** Responsive 16:9 video embed for a vendor gallery video URL. */
export default function VideoEmbed({ url, testId }) {
  const parsed = parseVideoUrl(url);
  if (!parsed) return null;

  if (!parsed.embedUrl) {
    return (
      <a
        href={parsed.fallback}
        target="_blank"
        rel="noreferrer"
        className="block aspect-video rounded-xl border border-dashed border-border/60 flex items-center justify-center text-xs text-muted-foreground px-2 text-center hover:bg-muted"
        data-testid={testId}
      >
        Open link
      </a>
    );
  }

  return (
    <div className="relative aspect-video rounded-xl overflow-hidden border border-border/60 bg-black/5" data-testid={testId}>
      <iframe
        src={parsed.embedUrl}
        title={`vendor-video-${parsed.type}`}
        loading="lazy"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
}
